import React, { Component, Dispatch } from 'react';
import { Calendar, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import addMonths from 'date-fns/addMonths';
import format from 'date-fns/format';
import { connect } from 'react-redux';

import './CheckoutDeliverNotBefore.scss';

import { AppState } from 'store';
import { DELIVERY_DATE_FORMAT, generateDispatchToPropsMap, getCurrentDate, scrollHelper } from 'helpers';
import { setDeliverNotBefore, SetDeliverNotBeforeFunc } from 'store/checkout/actions';
import { Scrollable, Validatable } from 'interfaces';

import { SofaButton } from '@sofology/sofologycomponents';

const MAX_MONTHS_NOT_BEFORE = 6;

interface OwnProps {
    ref?: React.RefObject<DeliverNotBeforeType>;
}

interface OwnState {
    chooseDeliveryDateMode: boolean;
    initialMode: boolean;
    date: Date | null;
    viewMode: boolean;
    showError: boolean;
    isAsSoonAsItsReadyMode: boolean;
}

interface StateProps {
    isUpdatingEca: boolean;
    deliverNotBefore: Date | null;
}

interface DispatchProps {
    setDeliverNotBefore: SetDeliverNotBeforeFunc;
}

class CheckoutDeliverNotBefore extends Component<OwnProps & StateProps & DispatchProps, OwnState> implements Validatable, Scrollable {
    private element = React.createRef<HTMLDivElement>();

    constructor(props: OwnProps & StateProps & DispatchProps) {
        super(props);

        this.state = {
            chooseDeliveryDateMode: false,
            initialMode: true,
            viewMode: false,
            date: null,
            showError: false,
            isAsSoonAsItsReadyMode: false
        };
    }

    componentDidUpdate(prevProps: Readonly<OwnProps & StateProps & DispatchProps>, prevState: Readonly<OwnState>): void {
        /** if call to the API was broken set previous date to state or show initial mode */
        if (this.props.deliverNotBefore !== this.state.date && this.state.date === prevState.date) {
            if (prevProps.deliverNotBefore === null) {
                this.setState({ date: prevProps.deliverNotBefore, initialMode: true, viewMode: false });
            } else {
                this.setState({ date: this.props.deliverNotBefore, initialMode: false, viewMode: true });
            }
        }
    }

    handleSetNotBeforeClick = () => {
        this.setState({ initialMode: false, chooseDeliveryDateMode: true, showError: false, isAsSoonAsItsReadyMode: false });
    };

    handleSoonItsReadyClick = () => {
        const currentDate = getCurrentDate();

        this.setState({ initialMode: false, viewMode: true, date: currentDate, showError: false, isAsSoonAsItsReadyMode: true });
        this.props.setDeliverNotBefore(currentDate, true);
    };

    handleEditClick = () => {
        this.setState({ viewMode: false, chooseDeliveryDateMode: true, isAsSoonAsItsReadyMode: false });
    };

    handleDateChange = (newDate: Date) => {
        this.setState({ date: newDate, chooseDeliveryDateMode: false, viewMode: true, showError: false });
        this.props.setDeliverNotBefore(newDate, false);
    };

    render(): JSX.Element {
        const { chooseDeliveryDateMode, initialMode, viewMode, isAsSoonAsItsReadyMode } = this.state;
        const title = isAsSoonAsItsReadyMode ? 'Great!' : 'Please don\'t deliver before...';

        return (
            <div className='deliver-not-before' ref={this.element}>
                <legend className='deliver-not-before__title'>{title}</legend>
                {
                    initialMode && this.renderInitialMode()
                }
                {
                    chooseDeliveryDateMode && this.renderDatePicker()
                }
                {
                    viewMode && this.renderView()
                }
            </div>
        );
    }

    renderInitialMode(): JSX.Element | null {
        const { showError } = this.state;

        return (
            <>
                <p className='deliver-not-before__notice'>
                    From time to time orders are ready sooner than we expect. If this happens and you are happy
                    to take delivery before our estimation please select 'I want it as soon as it's ready'.
                </p>
                <p className='deliver-not-before__notice'>
                    If you know that you can't take delivery early, perhaps because you're moving house or redecorating,
                    please set a 'not before' date.
                </p>
                <div className='deliver-not-before__actions'>
                    <SofaButton
                        text='Set "Not Before" Date'
                        variant='choice'
                        onClick={this.handleSetNotBeforeClick}
                        addErrorClass={showError}
                    />
                    <SofaButton
                        text={'I want it as soon as it\'s ready'}
                        variant='choice'
                        onClick={this.handleSoonItsReadyClick}
                        addErrorClass={showError}
                    />
                    {showError && this.renderErrorMessage()}
                </div>
            </>

        );
    }

    renderDatePicker(): JSX.Element {
        const { date, showError } = this.state;
        const currentDate = getCurrentDate();
        const errorMessage = showError ? <div className='deliver-not-before__error-choose-delivery-date-mode'>Please select your earliest delivery date</div> : null;
        const unselectedDateClassName = date ? '' : 'deliver-not-before__calendar-container--unselected-date';

        return (
            <>
                <p className='deliver-not-before__notice'>
                    Once your Order is ready, delivery must be taken within 7 days to avoid storage charges.
                    If you can't take delivery before a certain day, please set it below:
                </p>
                <div className={`deliver-not-before__calendar-container ${unselectedDateClassName}`}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <Calendar
                            date={date || currentDate}
                            onChange={this.handleDateChange}
                            minDate={currentDate}
                            maxDate={addMonths(currentDate, MAX_MONTHS_NOT_BEFORE)}
                        />
                    </MuiPickersUtilsProvider>
                </div>
                {errorMessage}
            </>
        );
    }

    renderErrorMessage(): JSX.Element {
        return (
            <div className='deliver-not-before__error'>Please select an option</div>
        );
    }

    renderView(): JSX.Element {
        const { date, isAsSoonAsItsReadyMode } = this.state;
        const currentDate = getCurrentDate();
        const target = isAsSoonAsItsReadyMode ? 'We\'ll be in touch as soon as it\'s ready.' : 'Not before ';
        const editButtonText = <>Edit <i className='sa sa-pencil-square-o'></i></>;

        return (
            <div>
                <div className='deliver-not-before__date'>
                    <p className='deliver-not-before__date__target'>
                        {target}
                        {isAsSoonAsItsReadyMode ? '' : <span>{format((date || currentDate), DELIVERY_DATE_FORMAT)}</span>}
                    </p>
                    <SofaButton
                        text={editButtonText}
                        variant='small'
                        onClick={this.handleEditClick}
                    />
                </div>
            </div>
        );
    }

    validate = (): boolean => {
        const { deliverNotBefore } = this.props;
        const { initialMode, chooseDeliveryDateMode } = this.state;
        let showError = false;

        if (initialMode || (chooseDeliveryDateMode && !deliverNotBefore)) {
            showError = true;
            this.scrollTo();
        }

        this.setState({ showError });

        return !showError;
    };

    scrollTo = () => {
        scrollHelper.scrollTo(this.element);
    };
}

function mapStateToProps(state: AppState): StateProps {
    return {
        isUpdatingEca: state.checkoutState.isUpdatingEca,
        deliverNotBefore: state.checkoutState.deliverNotBefore
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [setDeliverNotBefore]);
}

export default connect(mapStateToProps, mapDispatchToProps, undefined, { forwardRef: true })(CheckoutDeliverNotBefore);
export type DeliverNotBeforeType = CheckoutDeliverNotBefore;